import React from 'react';
import { 
  Activity, PieChart as PieChartIcon, ShieldCheck, CheckCircle, Target, 
  List, AlertTriangle, ShieldAlert, Sparkles, Brain, 
  Download, Loader2, GraduationCap, Compass, Users, 
  Briefcase, Smartphone, HeartPulse, Home
} from 'lucide-react';

import EditableAIBox from '../EditableAIBox';
import { calculateYasamPencerem } from '../../utils/YasamPencerem_engine';

// --- 1. BİREYSEL RAPOR ARAYÜZÜ ---
export const YasamPenceremReportUI = ({ data, meta }) => {
  const risks = data.risks || [];
  
    // ============================================================
  // YAŞAM PENCEREM - BİREYSEL RAPOR VERİLERİ
  // ============================================================

  // Eski kayıtlarla geriye dönük uyumluluk.
 const POSITIVE_IDS = new Set([26]);
const CONTEXT_IDS = new Set([3, 13, 17, 24, 29, 47]);

  const ATTENTION_IDS = new Set([
    3, 7, 9, 11, 14, 16, 18, 25, 33, 36
  ]);

  const VALID_CATEGORIES = new Set([
    "Aile ve Çevre",
    "Okul ve Eğitim",
    "Akran ve Sosyal Yaşam",
    "Kişisel Yaşam ve Duygular",
    "Gelecek ve Planlama"
  ]);

  const LEGACY_CATEGORY_MAP = {
    "Aile": "Aile ve Çevre",
    "Okul": "Okul ve Eğitim",
    "Sosyal": "Akran ve Sosyal Yaşam",
    "Benlik": "Kişisel Yaşam ve Duygular"
  };

  // Bütün işaretlemeleri koruyoruz.
  const allRisksFormatted = risks.map((r, index) => {

    const isObject =
      r !== null && typeof r === "object";

    const rawId = isObject ? Number(r.id) : NaN;

    const realId =
      Number.isInteger(rawId) &&
      rawId >= 1 &&
      rawId <= 48
        ? rawId
        : null;

    const rawText = isObject ? r.text : r;

    const cleanText = String(rawText ?? "")
      .replace(/[\[\(]?KRİTİK[\]\)]?/gi, "")
      .replace(/^[^a-zA-ZçÇğĞıİöÖşŞüÜ0-9]+/, "")
      .trim();

    const rawCategory =
      isObject ? r.category : "";

    const category =
      VALID_CATEGORIES.has(rawCategory)
        ? rawCategory
        : LEGACY_CATEGORY_MAP[rawCategory] ||
          "Diğer Bildirimler";

    const kind =
  realId === 13
    ? "context"
    : isObject &&
      ["positive", "context", "need"].includes(r.kind)
      ? r.kind
      : POSITIVE_IDS.has(realId)
        ? "positive"
        : CONTEXT_IDS.has(realId)
          ? "context"
          : "need";

    const attentionLevel =
      realId === 18
        ? "safety_review"
        : ATTENTION_IDS.has(realId)
          ? "review"
          : "routine";

    return {
      id: realId ?? `?${index + 1}`,
      text: cleanText,
      category,
      kind,
      attentionLevel,
      isCritical: attentionLevel !== "routine"
    };

  });


  // Yalnız problem/ihtiyaç bildirimleri.
  const problemItems = allRisksFormatted.filter(
    item => item.kind === "need"
  );


  // Problem alanlarının gerçek dağılımı.
  const categories = {};

  problemItems.forEach(item => {

    if (!categories[item.category]) {
      categories[item.category] = 0;
    }

    categories[item.category]++;

  });


  // Özel dikkat maddeleri, her biri yalnız bir kez.
  const criticalRisks = allRisksFormatted.filter(
    item => item.isCritical
  );

  const criticalCount = criticalRisks.length;


  // Güvenlik değerlendirme göstergesini diğer
  // işaretleme yoğunluklarından ayrı ele alıyoruz.
  const hasExtremeRisk = allRisksFormatted.some(
    item => item.attentionLevel === "safety_review"
  );

  const isRedAlarmState = hasExtremeRisk;


  const sortedCategories = Object.entries(categories)
    .sort((a, b) => b[1] - a[1]);

  const maxCategoryCount =
    sortedCategories.length > 0
      ? sortedCategories[0][1]
      : 1;

  const topCategoryName =
    sortedCategories.length > 0
      ? sortedCategories[0][0]
      : "Belirgin Problem Alanı Yok";


  // ============================================================
  // GERÇEKTEN BİLDİRİLEN OLUMLU KAYNAKLAR
  // ============================================================

  const strengths = allRisksFormatted
    .filter(item => item.kind === "positive")
    .map(item => ({
      name: `OLUMLU BİLDİRİM - MADDE ${item.id}`,
      desc: item.text,
      isZero: true,
      count: 0
    }));


  // ============================================================
  // GÖRÜŞMEDE DEĞERLENDİRİLECEK ALAN
  // ============================================================

  let psychDynamic = {
    title: "Görüşmede Değerlendirilecek Alan",
    desc:
      problemItems.length > 0
        ? `En çok işaretlenen problem alanı: ${topCategoryName}. ` +
          `Bu alandaki ${categories[topCategoryName]} bildirimin ` +
          `öğrenci için anlamı ve olası destek ihtiyacı ` +
          `bireysel görüşmede netleştirilmelidir.`
        : "Öğrenci bu formda problem bildirmemiştir. " +
          "Bu sonuç tek başına bütün yaşam alanlarında " +
          "güçlük bulunmadığını göstermez.",
    color: "text-indigo-700",
    bg: "bg-indigo-50 border-indigo-200"
  };


  if (hasExtremeRisk) {

    psychDynamic = {
      title: "Öncelikli Bireysel Değerlendirme",
      desc:
        "Güvenlik açısından özel dikkat gerektiren " +
        "bir bildirim bulunmaktadır. Öğrenciyle " +
        "gecikmeden görüşülerek durum, okulun ilgili " +
        "güvenlik ve yönlendirme prosedürleri " +
        "çerçevesinde değerlendirilmelidir.",
      color: "text-red-700",
      bg: "bg-red-50 border-red-200"
    };

  }


  // ============================================================
  // GÖRÜŞME ÖNCELİĞİ VE İZLEM
  // ============================================================

  let followUp = {
    title: "Rutin PDR İzlemi",
    desc:
      "Mevcut işaretlemeler tek başına özel bir " +
      "müdahale gerekliliği göstermez. Öğrencinin " +
      "görüşme talebi ve diğer bilgiler dikkate alınmalıdır.",
    color: "text-slate-700",
    bg: "bg-slate-50 border-slate-200"
  };


  if (hasExtremeRisk) {

    followUp = {
      title: "Gecikmeden Güvenlik Değerlendirmesi",
      desc:
        "İlgili bildirim bireysel görüşmede " +
        "gecikmeden değerlendirilmelidir. " +
        "Gerekli koruyucu adımlar okulun " +
        "yürürlükteki prosedürlerine göre belirlenmelidir.",
      color: "text-red-700",
      bg: "bg-red-50 border-red-200"
    };

  } else if (criticalCount > 0) {

    followUp = {
      title: "Öncelikli Bireysel Görüşme",
      desc:
        `${criticalCount} özel dikkat maddesi işaretlenmiştir. ` +
        "Bu bildirimlerin öğrencinin yaşamındaki " +
        "karşılığı bireysel görüşmede araştırılmalıdır. " +
        "İşaretleme sayısı tek başına klinik risk düzeyi değildir.",
      color: "text-orange-700",
      bg: "bg-orange-50 border-orange-200"
    };

  } else if (problemItems.length >= 15) {

    followUp = {
      title: "Çoklu Problem Bildirimi - Görüşme Önerisi",
      desc:
        `${problemItems.length} problem/ihtiyaç bildirimi vardır. ` +
        "Öğrencinin öncelik verdiği güçlükler " +
        "bireysel görüşmede belirlenerek uygun " +
        "PDR desteği planlanabilir.",
      color: "text-amber-700",
      bg: "bg-amber-50 border-amber-200"
    };

  }

  const getCategoryColor = (index) => {
    const colors = ['bg-indigo-600', 'bg-purple-500', 'bg-blue-400', 'bg-teal-500', 'bg-slate-400'];
    return colors[index % colors.length];
  };

  const isHighVolume = allRisksFormatted.length > 14;

  return (
    <div className="space-y-6 print:space-y-4 font-sans flex flex-col print:min-h-[260mm]">
      
      {/* KLİNİK ÜST BAŞLIK & ÖĞRENCİ BİLGİSİ */}
      <div className={`p-4 print:p-3 rounded-xl flex justify-between items-center shadow-sm print:bg-white print:text-black print:shadow-none print:border-b-2 print:border-slate-900 ${isRedAlarmState ? 'bg-red-900 text-white' : 'bg-slate-900 text-white'}`}>
        <div className="flex items-center gap-4 print:gap-3">
          <div className={`h-12 w-12 print:h-9 print:w-9 rounded-xl flex items-center justify-center shadow-inner ${isRedAlarmState ? 'bg-red-600' : 'bg-slate-700'}`}>
            <Activity className="h-6 w-6 print:h-4 print:w-4"/>
          </div>
          <div>
            <h2 className="text-xl print:text-lg font-bold leading-none mb-1">Yaşam Pencerem Analizi</h2>
            <span className="text-[10px] print:text-[10px] text-slate-300 print:text-slate-600 font-bold tracking-widest uppercase block">
              {meta?.studentClass || "Sınıf Belirtilmemiş"} • ÖĞRENCİ NO: {meta?.studentNo || "-"}
            </span>
          </div>
        </div>
        <div className="bg-white/10 px-4 py-2 print:px-3 print:py-1.5 rounded-lg text-center print:border print:border-slate-300">
          <span className={`text-2xl print:text-xl font-black block leading-none ${isRedAlarmState ? 'text-red-300' : 'text-slate-200'}`}>{risks.length}</span>
          <span className="text-[9px] print:text-[9px] block opacity-70 uppercase font-bold mt-0.5">İşaretlenen Madde</span>
        </div>
      </div>

      {/* 1. SATIR: GRAFİK VE KORUYUCU FAKTÖRLER */}
      <div className="grid grid-cols-1 md:grid-cols-2 print:grid-cols-2 gap-4 print:gap-4">
        {/* SOL: KATEGORİ DAĞILIMI */}
        <div className="bg-white p-5 print:p-4 rounded-2xl border border-slate-200 shadow-sm flex flex-col print:border-slate-300">
          <h3 className="font-bold text-slate-800 mb-4 print:mb-2.5 flex items-center text-sm print:text-[11px] border-b pb-2 print:pb-1.5">
            <PieChartIcon className="h-4 w-4 print:h-3.5 print:w-3.5 mr-2 text-indigo-600"/> Problem Alanları Dağılımı
          </h3>
          <div className="space-y-4 print:space-y-2 flex-1 flex flex-col justify-center">
            {sortedCategories.length > 0 ? sortedCategories.map(([name, count], i) => (
              <div key={name}>
                <div className="flex justify-between items-end mb-1 print:mb-0.5">
                  <span className="text-[10px] print:text-[8px] font-bold text-slate-600 uppercase tracking-wide">{name}</span>
                  <span className="text-xs print:text-[9px] font-black text-slate-800">{count} Madde</span>
                </div>
                <div className="h-2 print:h-1.5 w-full bg-slate-100 rounded-full overflow-hidden print:border print:border-slate-300">
                  <div className={`h-full rounded-full print:bg-slate-700 ${getCategoryColor(i)}`} style={{ width: `${(count / maxCategoryCount) * 100}%` }}></div>
                </div>
              </div>
            )) : (
              <p className="text-xs text-slate-400 italic">Bulgu tespit edilmedi.</p>
            )}
          </div>
        </div>

        {/* SAĞ: KORUYUCU FAKTÖRLER */}
        <div className="bg-emerald-50 p-5 print:p-4 rounded-2xl border border-emerald-100 shadow-sm flex flex-col print:bg-white print:border-slate-300">
          <div className="flex items-center mb-4 print:mb-2 border-b border-emerald-200 print:border-slate-200 pb-3 print:pb-1.5">
            <ShieldCheck className="h-5 w-5 print:h-4 print:w-4 text-emerald-600 mr-2"/>
            <div>
             <span className="text-xs print:text-[10.5px] font-black text-emerald-900 uppercase tracking-widest block leading-none">Bildirilen Olumlu Kaynaklar</span>
<span className="text-[9px] print:text-[8px] font-medium text-emerald-600 uppercase tracking-wide mt-0.5 block">Öğrencinin İşaretlediği Olumlu İfadeler</span>
            </div>
          </div>
          
          <div className="flex-1 flex flex-col justify-center space-y-3 print:space-y-2">
            {strengths.length > 0 ? (
              strengths.slice(0, 2).map((strength, idx) => (
                <div key={idx} className="bg-white p-3 print:p-2.5 rounded-xl border border-emerald-100/50 shadow-sm print:border-slate-200">
                  <div className="flex justify-between items-center mb-1.5 print:mb-1">
                    <span className="text-[9px] print:text-[8px] font-bold text-slate-400 uppercase tracking-wider">{strength.name}</span>
                    <span className={`text-[8px] print:text-[7.5px] font-black px-1.5 py-0.5 rounded ${strength.isZero ? 'bg-emerald-100 text-emerald-700' : 'bg-blue-50 text-blue-600'}`}>
                      {'İŞARETLENDİ'}
                    </span>
                  </div>
                  <div className="flex items-start">
                    <div className={`min-w-[4px] w-1 h-auto self-stretch rounded-full mr-2 print:bg-slate-400 ${strength.isZero ? 'bg-emerald-500' : 'bg-blue-400'}`}></div>
                    <p className="text-[10px] print:text-[9px] text-slate-700 leading-snug font-medium text-justify">
                      {strength.desc}
                    </p>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-4">
                <Activity className="h-8 w-8 text-slate-300 mx-auto mb-2 opacity-50"/>
                <p className="text-[10px] text-slate-500 mt-1">Bu uygulamada olumlu öz bildirim işaretlenmemiştir.</p>
              </div>
            )}
          </div>
        </div>
      </div>

    {/* 2. SATIR: STRATEJİ VE TAKİP KARTLARI (YENİLENMİŞ) */}
      <div className="grid grid-cols-1 md:grid-cols-2 print:grid-cols-2 gap-4 print:gap-4">
          <div className={`p-5 print:p-3.5 rounded-2xl border shadow-sm flex flex-col justify-center ${psychDynamic.bg}`}>
              <div className="flex items-center mb-1">
                  <span className="text-xs print:text-[10px] font-black uppercase tracking-widest text-slate-800">Görüşmede Değerlendirilecek Alan</span>
              </div>
              <div className={`text-sm print:text-[11.5px] font-black mb-1 mt-1 ${psychDynamic.color}`}>{psychDynamic.title}</div>
              <p className="text-[13px] print:text-[9.5px] font-medium text-slate-700 leading-snug">{psychDynamic.desc}</p>
          </div>
          <div className={`p-5 print:p-3.5 rounded-2xl border shadow-sm flex flex-col justify-center ${followUp.bg}`}>
              <div className="flex items-center mb-1">
                  <span className="text-xs print:text-[10px] font-black uppercase tracking-widest text-slate-800">Görüşme Önceliği ve İzlem</span>
              </div>
              <div className={`text-sm print:text-[11.5px] font-black mb-1 mt-1 ${followUp.color}`}>{followUp.title}</div>
              <p className="text-[13px] print:text-[9.5px] font-medium text-slate-700 leading-snug">{followUp.desc}</p>
          </div>
      </div>

      {/* 3. SATIR: TAM GENİŞLİKTE LİSTE */}
      <div className="bg-slate-50 p-5 print:p-4 rounded-2xl border border-slate-200 print:bg-white print:border-t-2 print:border-slate-900 print:rounded-none print:shadow-none flex-1 mt-2">
        <h3 className="font-bold text-slate-800 mb-4 print:mb-3 flex items-center text-sm print:text-[11px] border-b pb-3 print:pb-2">
          <List className="h-4 w-4 mr-2 text-slate-500"/> Tüm İşaretlenen Bulgular ({allRisksFormatted.length})
        </h3>
        
        {allRisksFormatted.length > 0 ? (
          <div className={`grid grid-cols-1 md:grid-cols-2 ${isHighVolume ? 'print:grid-cols-3 print:gap-1.5' : 'print:grid-cols-2 print:gap-2.5'} gap-3`}>
            {allRisksFormatted.map((item, i) => (
              <div key={i} className={`flex items-start p-3 ${isHighVolume ? 'print:p-1.5' : 'print:p-2'} bg-white border rounded-xl print:rounded-lg shadow-sm transition-colors ${item.isCritical ? 'border-red-200 bg-red-50/30' : 'border-slate-200'}`}>
                <div className={`min-w-[24px] h-6 ${isHighVolume ? 'print:min-w-[14px] print:h-3.5 print:text-[7px]' : 'print:min-w-[18px] print:h-4.5 print:text-[8px]'} rounded flex items-center justify-center text-[10px] font-black mr-3 ${isHighVolume ? 'print:mr-1.5' : 'print:mr-2'} border ${item.isCritical ? 'bg-red-100 text-red-700 border-red-200' : 'bg-slate-100 text-slate-600 border-slate-200'}`}>
                  {item.id}
                </div>
                <div className="flex-1">
                  <div className="flex justify-between items-center mb-1 print:mb-[1px]">
                    <span className={`block text-[8px] ${isHighVolume ? 'print:text-[6.5px]' : 'print:text-[7.5px]'} font-bold text-slate-400 uppercase tracking-wider`}>{item.category}</span>
                    {item.isCritical && <span className={`text-[8px] ${isHighVolume ? 'print:text-[6px] px-1 py-[1px]' : 'print:text-[7px] px-1.5 py-0.5'} bg-red-100 text-red-700 rounded font-black tracking-widest uppercase shadow-sm`}>Kritik</span>}
                  </div>
                  <span className={`text-[11px] ${isHighVolume ? 'print:text-[8px] leading-tight' : 'print:text-[9.5px] leading-snug'} text-slate-800 font-medium block`}>{item.text}</span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-xs text-slate-400 italic text-center py-4 print:py-2">Öğrenci tarafından hiçbir madde işaretlenmemiştir.</p>
        )}
      </div>
    </div>
  );
};


// ============================================================
// YAŞAM PENCEREM - KURUMSAL HESAPLAMA MOTORU
// ============================================================

export const aggregateYasamPencerem = (subs = []) => {

  // 1. Aynı öğrencinin son uygulamasını seç.
  const latestByStudent = new Map();

  const getTimestamp = (sub) => {
    const value =
      sub?.timestamp ??
      sub?.updatedAt ??
      sub?.createdAt;

    if (typeof value?.toMillis === "function") {
      return value.toMillis();
    }

    if (typeof value?.seconds === "number") {
      return value.seconds * 1000;
    }

    if (typeof value === "number") {
      return value;
    }

    const parsed = Date.parse(value);
    return Number.isFinite(parsed) ? parsed : 0;
  };

  subs.forEach((sub, index) => {

    const studentNo = String(
      sub?.studentNo ?? sub?.answers?.[302] ?? ""
    ).trim();

    const key = studentNo
      ? `student:${studentNo}`
      : `record:${sub?.firebaseId ?? sub?.id ?? index}`;

    const existing = latestByStudent.get(key);

    if (
      !existing ||
      getTimestamp(sub) > getTimestamp(existing)
    ) {
      latestByStudent.set(key, sub);
    }

  });

  const uniqueSubs = [...latestByStudent.values()];
  const total = uniqueSubs.length;

  if (total === 0) return null;


  // 2. Problem alanlarının merkezi kategorileri.
  const catCounts = {
    "Aile ve Çevre": 0,
    "Okul ve Eğitim": 0,
    "Akran ve Sosyal Yaşam": 0,
    "Kişisel Yaşam ve Duygular": 0,
    "Gelecek ve Planlama": 0
  };

  const positiveIds = new Set([26]);
const contextIds = new Set([3, 13, 17, 24, 29, 47]);

  const attentionIds = new Set([
    3, 7, 9, 11, 14, 16, 18, 25, 33, 36
  ]);

  const legacyCategories = {
    Aile: "Aile ve Çevre",
    Okul: "Okul ve Eğitim",
    Sosyal: "Akran ve Sosyal Yaşam",
    Benlik: "Kişisel Yaşam ve Duygular"
  };

  const itemMap = new Map();
  const classMap = {};

  let redAlarmStudents = 0;
  let attentionStudentCount = 0;
  let totalCheckedItems = 0;
  let totalProblemItems = 0;


  // 3. Öğrencileri değerlendir.
  uniqueSubs.forEach(sub => {

    const hasAnswers =
      sub?.answers &&
      typeof sub.answers === "object" &&
      !Array.isArray(sub.answers);

    // Eski kayıtları da güncel motorla hesapla.
    // Firebase'deki orijinal cevaplar değişmez.
    const result = hasAnswers
      ? calculateYasamPencerem(sub.answers)
      : (sub.reportData || {});

    const markedItems = Array.isArray(result.risks)
      ? result.risks
      : [];

    const className = String(
      sub.studentClass ||
      sub.sinif ||
      sub.class ||
      sub.grade ||
      "Sınıf Belirtilmemiş"
    ).trim();

    if (!classMap[className]) {
      classMap[className] = {
        name: className,
        studentCount: 0,
        riskCount: 0
      };
    }

    classMap[className].studentCount++;

    let studentProblemCount = 0;
    let hasAttention = false;
    let hasSafetyReview = false;

    const seenIds = new Set();

    markedItems.forEach(item => {

      const id = Number(item?.id);

      if (
        !Number.isInteger(id) ||
        id < 1 ||
        id > 48 ||
        seenIds.has(id)
      ) {
        return;
      }

      seenIds.add(id);

    const kind =
  id === 13
    ? "context"
    : ["need", "positive", "context"].includes(item.kind)
      ? item.kind
      : positiveIds.has(id)
        ? "positive"
        : contextIds.has(id)
          ? "context"
          : "need";

      const rawCategory = String(
        item.category || "Diğer Bildirimler"
      );

      const category =
        legacyCategories[rawCategory] ||
        rawCategory;

      const isCritical = attentionIds.has(id);

      const cleanText = String(item.text ?? `Madde ${id}`)
        .replace(/[\[\(]?KRİTİK[\]\)]?/gi, "")
        .trim();

      if (!itemMap.has(id)) {
        itemMap.set(id, {
          id,
          text: cleanText,
          category,
          kind,
          isCritical,
          count: 0
        });
      }

      itemMap.get(id).count++;
      totalCheckedItems++;

      // Yalnız ihtiyaç bildirimlerini problem say.
      if (kind === "need") {

        studentProblemCount++;
        totalProblemItems++;

        if (catCounts[category] === undefined) {
          catCounts[category] = 0;
        }

        catCounts[category]++;
      }

      if (isCritical) {
        hasAttention = true;
      }

      if (id === 18) {
        hasSafetyReview = true;
      }

    });

    classMap[className].riskCount += studentProblemCount;

    if (hasAttention) {
      attentionStudentCount++;
    }

    if (hasSafetyReview) {
      redAlarmStudents++;
    }

  });


  // 4. Madde yaygınlıkları.
  const allItems = [...itemMap.values()]
    .map(item => ({
      ...item,
      percent: Math.round(
        (item.count / total) * 100
      )
    }))
    .sort(
      (a, b) =>
        b.count - a.count ||
        a.id - b.id
    );

  const criticalItems = allItems.filter(
    item => item.isCritical
  );

  const safeZones = allItems.filter(
    item => item.kind === "positive"
  );

  const topCategories = Object.entries(catCounts)
    .sort((a, b) => b[1] - a[1]);

  const dominantCategory =
    topCategories[0]?.[1] > 0
      ? topCategories[0][0]
      : "Belirgin Problem Alanı Yok";

  const secondaryCategory =
    topCategories[1]?.[1] > 0
      ? topCategories[1][0]
      : "Belirsiz";

  const avgProblemsPerStudent =
    (totalProblemItems / total).toFixed(1);


  // 5. Sınıf bazında bildirim yoğunluğu.
  const classStats = Object.values(classMap)
    .map(c => ({
      ...c,
      riskScore:
        (c.riskCount / c.studentCount).toFixed(1)
    }))
    .sort(
      (a, b) =>
        Number(b.riskScore) -
        Number(a.riskScore)
    );


  // 6. Mevcut rapor arayüzüyle uyumlu sonuç.
  return {
    total,
    redAlarmStudents,
    attentionStudentCount,
    catCounts,
    topCategories,
    allItems,
    criticalItems,
    dominantCategory,
    secondaryCategory,
    avgProblemsPerStudent,
    safeZones,
    totalCheckedItems,
    totalProblemItems,
    classStats
  };

};

// --- 3. KURUMSAL RAPOR ARAYÜZÜ (DEĞİŞİKLİK YOK, AYNEN KALDI) ---
export const YasamPenceremKurumsalUI = ({ title, subs, aiResult, onGenerateAI, aiLoading, handlePrint, downloading }) => {
  const data = aggregateYasamPencerem(subs);

if (!data) {
  return (
    <div className="p-8 text-center text-slate-500">
      Bu gruba ait veri bulunamadı.
    </div>
  );
}

// Sınıf sıralamasında asgari katılımcı koşulu.
// Bu sınır yalnızca karşılaştırmalı gösterime uygulanır.
const MIN_CLASS_PARTICIPANTS = 10;

const rankedClasses = data.classStats.filter(
  c => Number(c.studentCount) >= MIN_CLASS_PARTICIPANTS
);

    const catColors = {
    "Aile ve Çevre": "bg-indigo-500",
    "Okul ve Eğitim": "bg-blue-500",
    "Akran ve Sosyal Yaşam": "bg-teal-500",
    "Kişisel Yaşam ve Duygular": "bg-purple-500",
    "Gelecek ve Planlama": "bg-amber-500"
  };
  const totalCatHits = data.topCategories.reduce((acc, curr) => acc + curr[1], 0) || 1;
    const top10Items = data.allItems.slice(0, 10);

  // ============================================================
  // YAŞAM PENCEREM - KURUMSAL AI VERİ PAKETİ
  // ============================================================

  // Öğrenci kimliği içermeyen toplu veriler hazırlanır.
  // Genel AI aktarımı ilk 15 girdiyi kullandığından
  // bu paket tam 15 girdiden oluşur.

  const formatAiItems = (items, limit = 10) => {

    const rows = items.slice(0, limit).map(item =>
      `Madde ${item.id}: ${item.text} — ${item.count} öğrenci (%${item.percent})`
    );

    return rows.length
      ? rows.join(" | ")
      : "İşaretleme bulunmuyor";
  };


  const otherAttentionCount = Math.max(
    0,
    data.attentionStudentCount - data.redAlarmStudents
  );

  const otherParticipantCount = Math.max(
    0,
    data.total - data.attentionStudentCount
  );


  const aiSummaryItems = [

    "ARAÇ: Yaşam Pencerem, 48 maddelik işaretleme temelli PDR ihtiyaç taraması; psikometrik tanı/şiddet ölçeği değildir.",

    `RAPOR KAPSAMI: ${title}.`,

    `TEKİL KATILIMCI: ${data.total} öğrenci.`,

    `PROBLEM/İHTİYAÇ: ${data.totalProblemItems} işaretleme; öğrenci başına ortalama ${data.avgProblemsPerStudent} işaretleme. Olumlu ve bağlamsal maddeler bu hesaba dahil değildir.`,

    `GÖRÜŞME GRUPLARI: güvenlik değerlendirmesi bildirimi ${data.redAlarmStudents}; diğer özel dikkat ${otherAttentionCount}; kalan katılımcılar ${otherParticipantCount}. Bu gruplar tanı değildir.`,

    ...data.topCategories.map(([category, count]) =>
      `İHTİYAÇ ALANI — ${category}: ${count} işaretleme, bütün problem/ihtiyaç işaretlemelerinin %${Math.round((count / Math.max(1, data.totalProblemItems)) * 100)}'i. Bu yüzde öğrenci oranı değildir.`
    ),

    `EN SIK BİLDİRİLEN 5 PROBLEM MADDESİ: ${formatAiItems(
      data.allItems.filter(item => item.kind === "need"),
      5
    )}`,

    `ÖZEL DİKKAT MADDELERİ: ${formatAiItems(
      data.criticalItems,
      10
    )}`,

    `BAĞLAMSAL BİLDİRİMLER: ${formatAiItems(
      data.allItems.filter(item => item.kind === "context"),
      6
    )}`,

    `DOĞRUDAN OLUMLU BİLDİRİMLER: ${formatAiItems(
      data.safeZones,
      2
    )}`,

    "YORUM SINIRI: Bütün sonuçlar işaretleme sıklığıdır. İşaretlenmeyen maddeler olumlu kaynak kanıtı değildir; grup verilerinden kişisel neden, tanı veya kesin kriz çıkarılamaz."

  ];

  const isRedAlarm = data.redAlarmStudents > 0;

  const redAlarmPercent = data.total > 0 ? Math.round((data.redAlarmStudents / data.total) * 100) : 0;

  const isHighVolumeTop10 = top10Items.length > 6; 
  const isHighVolumeCritical = data.criticalItems.length > 6; 

  return (
    <>
      <div className="flex justify-end mb-4 no-print px-2">
         <button onClick={() => handlePrint('yasam-kurumsal-area')} disabled={downloading} className="bg-blue-600 text-white px-5 py-2.5 rounded-xl font-bold flex items-center text-sm shadow-md hover:bg-blue-700 transition">
            {downloading ? <Loader2 className="animate-spin h-5 w-5 mr-2"/> : <Download className="h-5 w-5 mr-2"/>} Raporu Yazdır / PDF
         </button>
      </div>

      <div id="yasam-kurumsal-area" className="font-sans flex flex-col bg-white">
        
        {/* ========================================================= */}
        {/* SAYFA 1: BAŞLIK, KPI, DAĞILIM, İLK 10 MADDE VE İŞLEVSEL KARTLAR */}
        {/* ========================================================= */}
        <div className="flex flex-col gap-5 print:gap-5 mb-6 print:mb-0">
          
          <div className={`p-5 print:p-4 rounded-2xl flex justify-between items-center shadow-sm print:bg-white print:text-black print:shadow-none print:border-b-2 print:border-slate-900 ${isRedAlarm ? 'bg-red-900 text-white' : 'bg-slate-900 text-white'} shrink-0 avoid-break`}>
            <div className="flex items-center gap-5 print:gap-4">
              <div className={`h-14 w-14 print:h-12 print:w-12 rounded-xl flex items-center justify-center shadow-inner ${isRedAlarm ? 'bg-red-600' : 'bg-slate-700'}`}>
                <Activity className="h-7 w-7 print:h-6 print:w-6"/>
              </div>
              <div>
                <h2 className="text-2xl print:text-xl font-black leading-none mb-1.5">{title}</h2>
                <span className="text-xs print:text-[11px] text-slate-300 print:text-slate-600 font-bold tracking-widest uppercase block">
                   YAŞAM PENCEREM • PDR İHTİYAÇ VE GÖRÜŞME ÖNCELİĞİ ANALİZİ
                </span>
              </div>
            </div>
            <div className="bg-white/10 px-5 py-2.5 print:px-4 print:py-2 rounded-xl text-center print:border print:border-slate-300">
              <span className={`text-3xl print:text-2xl font-black block leading-none ${isRedAlarm ? 'text-red-300' : 'text-slate-800'}`}>{data.total}</span>
              <span className="text-[10px] print:text-[9px] block opacity-70 uppercase font-bold mt-1">Katılımcı</span>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 print:grid-cols-3 gap-5 print:gap-4 shrink-0 avoid-break">
            <div className={`p-5 print:p-4 rounded-2xl border shadow-sm flex flex-col justify-center print:bg-white print:border-slate-300 ${isRedAlarm ? 'border-red-200' : 'border-slate-200'}`}>
              <div className="flex items-center mb-1.5">
                 <AlertTriangle className={`h-6 w-6 print:h-5 print:w-5 mr-2 ${isRedAlarm ? 'text-red-500' : 'text-emerald-500'}`}/>
                 <span className={`text-sm print:text-[11px] font-black uppercase tracking-widest ${isRedAlarm ? 'text-red-600' : 'text-emerald-600'}`}>Güvenlik Değerlendirmesi Bildirimi</span>
              </div>
              <p className={`text-2xl print:text-xl font-black mt-1 ${isRedAlarm ? 'text-red-700' : 'text-emerald-700'}`}>
                 {data.redAlarmStudents} <span className="text-xs print:text-[10px] font-medium opacity-70 text-slate-600 uppercase">Öğrenci</span>
              </p>
            </div>
            <div className="bg-indigo-50 p-5 print:p-4 rounded-2xl border border-indigo-100 print:bg-white print:border-slate-300 shadow-sm flex flex-col justify-center">
              <div className="flex items-center mb-1.5">
                 <Target className="h-6 w-6 print:h-5 print:w-5 mr-2 text-indigo-600"/>
                 <span className="text-sm print:text-[11px] font-black text-indigo-600 uppercase tracking-widest">En Sık Bildirilen İhtiyaç Alanı</span>
              </div>
              <p className="text-base print:text-sm font-black text-indigo-900 mt-1 leading-tight truncate">{data.dominantCategory}</p>
            </div>
            <div className="bg-amber-50 p-5 print:p-4 rounded-2xl border border-amber-100 print:bg-white print:border-slate-300 shadow-sm flex flex-col justify-center">
              <div className="flex items-center mb-1.5">
                 <Activity className="h-6 w-6 print:h-5 print:w-5 mr-2 text-amber-600"/>
                 <span className="text-sm print:text-[11px] font-black text-amber-600 uppercase tracking-widest">Ortalama Problem / İhtiyaç Bildirimi</span>
              </div>
              <p className="text-2xl print:text-xl font-black text-amber-900 mt-1">
                 {data.avgProblemsPerStudent} <span className="text-xs print:text-[10px] font-medium opacity-70 text-slate-600 uppercase">Madde / Öğrenci</span>
              </p>
            </div>
          </div>

          <div className="bg-slate-50 p-6 print:p-5 rounded-3xl border border-slate-200 print:bg-white print:border-slate-300 shadow-sm print:shadow-none shrink-0 avoid-break">
             <h3 className="font-bold text-slate-800 mb-4 print:mb-3 flex items-center text-base print:text-xs border-b border-slate-200 pb-2 uppercase tracking-widest">
               <PieChartIcon className="h-5 w-5 print:h-4 print:w-4 mr-2 text-blue-500"/> Bildirilen Problem ve İhtiyaç Alanları
             </h3>

<p className="text-[10px] print:text-[9px] text-slate-500 mb-3 leading-relaxed">
  Yüzdeler, toplam problem/ihtiyaç işaretlemeleri içindeki
  payı göstermektedir; öğrenci oranı değildir.
</p>

<div className="grid grid-cols-1 md:grid-cols-2 print:grid-cols-2 gap-x-10 gap-y-3.5 print:gap-y-3">
               {data.topCategories.filter(c => c[1] > 0).map(([catName, count]) => {
                 const pct = Math.round((count / totalCatHits) * 100);
                 return (
                   <div key={catName}>
                     <div className="flex justify-between items-end mb-1.5 print:mb-1">
                       <span className="text-sm print:text-[11px] font-bold text-slate-700 truncate mr-2">{catName}</span>
                       <span className="text-sm print:text-[11px] font-black text-slate-900">%{pct} <span className="text-xs print:text-[9px] text-slate-500 font-medium">({count})</span></span>
                     </div>
                     <div className="h-2.5 print:h-2 w-full bg-slate-200 print:bg-slate-100 rounded-full overflow-hidden print:border print:border-slate-200">
                       <div className={`h-full rounded-full print:bg-slate-600 ${catColors[catName] || 'bg-slate-500'}`} style={{ width: `${pct}%` }}></div>
                     </div>
                   </div>
                 );
               })}
             </div>
          </div>

          <div className="bg-white p-5 print:p-0 rounded-3xl border border-slate-200 print:border-none shadow-sm print:shadow-none shrink-0 avoid-break">
            <h3 className="font-bold text-slate-800 mb-4 print:mb-3 flex items-center text-base print:text-xs border-b border-slate-200 print:border-slate-300 pb-2.5 uppercase tracking-widest">
              <List className="h-5 w-5 print:h-4 print:w-4 mr-2 text-slate-500"/> En Sık İşaretlenen İlk 10 Madde
            </h3>
            
            {top10Items.length > 0 ? (
              <div className={`grid grid-cols-1 md:grid-cols-2 ${isHighVolumeTop10 ? 'print:grid-cols-2' : 'print:grid-cols-2'} gap-3 print:gap-3`}>
                {top10Items.map((item, index) => (
                  <div key={item.id} className="flex items-center p-3 print:p-2 bg-slate-50 border border-slate-200 print:border-slate-300 rounded-xl shadow-sm print:shadow-none avoid-break">
                    <div className="w-8 h-8 print:w-7 print:h-7 rounded-lg flex items-center justify-center bg-white print:bg-slate-100 text-slate-600 font-black text-sm print:text-[11px] border border-slate-200 shrink-0 mr-3 print:mr-2.5">
                      {index + 1}
                    </div>
                    <div className="flex-1 min-w-0 pr-2">
                      <p className="text-[10px] print:text-[9px] font-bold text-slate-400 uppercase truncate mb-0.5">
  {item.kind === "positive"
    ? "Olumlu Kaynak Bildirimi"
    : item.kind === "context"
      ? "Bağlamsal Bilgi"
      : item.category}
</p>
                      <p className="text-sm print:text-xs font-bold text-slate-800 leading-snug line-clamp-1">{item.text}</p>
                    </div>
                    <div className="shrink-0 text-right pl-3 print:pl-2.5 border-l border-slate-200">
                      <p className="text-base print:text-sm font-black text-blue-600 print:text-slate-800">%{item.percent}</p>
                      <p className="text-[10px] print:text-[9px] font-bold text-slate-500">{item.count} Kişi</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm print:text-xs text-slate-500 text-center py-4">İşaretlenmiş madde bulunmamaktadır.</p>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 print:grid-cols-2 gap-4 print:gap-4 shrink-0 avoid-break">
            <div className="bg-slate-50 p-4 print:p-4 rounded-2xl border border-slate-200 print:bg-white print:border-slate-300 shadow-sm print:shadow-none flex items-start gap-3 print:gap-2.5">
             <div className={`shrink-0 p-3 print:p-2 rounded-xl ${
  data.redAlarmStudents > 0
    ? 'bg-red-100 text-red-600 print:bg-red-50'
    : data.attentionStudentCount > 0
      ? 'bg-amber-100 text-amber-700 print:bg-amber-50'
      : 'bg-emerald-100 text-emerald-600 print:bg-emerald-50'
}`}>
  {data.attentionStudentCount > 0
    ? <ShieldAlert className="h-6 w-6 print:h-5 print:w-5"/>
    : <ShieldCheck className="h-6 w-6 print:h-5 print:w-5"/>}
</div>

<div>
  <h3 className="font-bold text-slate-800 text-xs print:text-[10px] uppercase tracking-widest mb-1">
    Önerilen PDR Önceliği
  </h3>

  <p className={`text-base print:text-xs font-black leading-tight ${
    data.redAlarmStudents > 0
      ? 'text-red-700'
      : data.attentionStudentCount > 0
        ? 'text-amber-700'
        : 'text-emerald-700'
  }`}>
    {data.redAlarmStudents > 0
      ? 'Gecikmeden Güvenlik Değerlendirmesi'
      : data.attentionStudentCount > 0
        ? 'Öncelikli Bireysel Görüşmeler'
        : 'Gelişimsel / Önleyici Rehberlik'}
  </p>

  <p className="text-xs print:text-[10px] text-slate-500 mt-1 leading-snug line-clamp-2">
    {data.redAlarmStudents > 0
      ? 'Özel güvenlik bildirimi bulunan öğrencilerin durumları bireysel olarak ve gecikmeden değerlendirilmelidir.'
      : data.attentionStudentCount > 0
        ? `${data.attentionStudentCount} öğrencinin özel dikkat gerektiren bildirimleri görüşme yoluyla değerlendirilmelidir.`
        : 'Mevcut veriler ışığında grup rehberliği ve rutin izlem çalışmaları planlanabilir.'}
  </p>
</div>
            </div>

            <div className="bg-slate-50 p-4 print:p-4 rounded-2xl border border-slate-200 print:bg-white print:border-slate-300 shadow-sm print:shadow-none flex items-start gap-3 print:gap-2.5">
              <div className="shrink-0 bg-blue-100 text-blue-600 print:bg-blue-50 p-3 print:p-2 rounded-xl">
                <Compass className="h-6 w-6 print:h-5 print:w-5"/>
              </div>
              <div>
                <h3 className="font-bold text-slate-800 text-xs print:text-[10px] uppercase tracking-widest mb-1">Stratejik Gözlem Alanı</h3>
                <p className="text-base print:text-xs font-black text-blue-700 leading-tight">
  {data.dominantCategory}
</p>

<p className="text-xs print:text-[10px] text-slate-500 mt-1 leading-snug">
  {data.dominantCategory === "Aile ve Çevre"
    ? "Aile ve ev yaşamına ilişkin bildirimler incelenerek veli çalışmaları için ihtiyaç alanları belirlenebilir."
    : data.dominantCategory === "Okul ve Eğitim"
      ? "Öğrencilerin okul ve eğitim ortamına ilişkin bildirimleri, okul iyileştirme çalışmalarında değerlendirilebilir."
      : data.dominantCategory === "Akran ve Sosyal Yaşam"
        ? "Akran ilişkileri ve sosyal yaşama ilişkin bildirimler üzerinden uygun grup rehberliği etkinlikleri planlanabilir."
        : data.dominantCategory === "Kişisel Yaşam ve Duygular"
          ? "Öğrencilerin kişisel ve duygusal ihtiyaçları bireysel görüşmelerde ayrıntılandırılabilir."
          : data.dominantCategory === "Gelecek ve Planlama"
            ? "Gelecek planlama ve kariyer rehberliği etkinliklerine yönelik ihtiyaçlar değerlendirilebilir."
            : "Bu grupta öne çıkan belirgin bir problem alanı bulunmamaktadır."}
</p>
              </div>
            </div>
          </div>

        </div>
        {/* ==================== 1. SAYFA SONU ==================== */}

        <div className="hidden print:block" style={{ pageBreakBefore: 'always' }}></div>

        {/* ========================================================= */}
        {/* SAYFA 2: YENİ, TEKRARSIZ KARTLAR VE KRİTİK MADDELER RADARI */}
        {/* ========================================================= */}
        <div className="flex flex-col gap-5 print:gap-5 mt-6 print:mt-0 mb-6 print:mb-0">
          
          <div className="grid grid-cols-2 gap-5 print:gap-4 shrink-0 avoid-break">
            
            {/* KART 1: KARE - ŞUBE/SINIF KIRILGANLIK RADARI */}
            <div className="col-span-1 bg-blue-50 p-5 print:p-5 rounded-2xl border border-blue-100 print:bg-white print:border-slate-300 flex flex-col justify-center shadow-sm print:shadow-none min-h-[140px]">
               <h3 className="font-bold text-blue-900 mb-3 print:mb-2.5 flex items-center text-xs print:text-[11px] uppercase tracking-widest border-b border-blue-200 print:border-slate-200 pb-2">
                 <Target className="h-5 w-5 print:h-4 print:w-4 mr-2 text-blue-600"/> Bildirim Yoğunluğu Yüksek Sınıflar
               </h3>
               <div className="space-y-2.5 print:space-y-2 mt-1">
                 <div>
  <span className="text-[10px] print:text-[9px] font-bold text-blue-500 uppercase tracking-widest block mb-0.5">
    En Yüksek Ortalama
  </span>

  <p className="text-sm print:text-xs font-black text-slate-800 leading-tight truncate">
    {rankedClasses[0]
      ? rankedClasses[0].name
      : 'Başka sınıf bulunmuyor'}

    {rankedClasses[0] && (
      <span className="block text-[10px] print:text-[9px] text-slate-500 font-medium mt-0.5">
        Katılımcı: {rankedClasses[0].studentCount} öğrenci
      </span>
    )}

    {rankedClasses[0] && (
      <span className="text-[10px] print:text-[9px] font-medium text-slate-500 ml-1">
        ({rankedClasses[0].riskScore} bildirim/öğrenci)
      </span>
    )}
  </p>
</div>
                  <div>
                    <span className="text-[10px] print:text-[9px] font-bold text-slate-500 uppercase tracking-widest block mb-0.5">İkinci Yüksek Ortalama</span>
                    <p className="text-xs print:text-[11px] font-black text-slate-700 leading-tight truncate">
                      {rankedClasses[1] ? rankedClasses[1].name : 'Başka sınıf bulunmuyor'}
                      {rankedClasses[1] && (
  <span className="block text-[10px] print:text-[9px] text-slate-500 font-medium mt-0.5">
    Katılımcı: {rankedClasses[1].studentCount} öğrenci
  </span>
)}
                      {rankedClasses[1] && <span className="text-[10px] print:text-[9px] font-medium text-slate-500 ml-1">({rankedClasses[1].riskScore} bildirim/öğrenci)</span>}
                    </p>
                  </div>
               </div>
              {/* SINIF KARŞILAŞTIRMA AÇIKLAMASI */}
               <p className="text-[9px] print:text-[8px] text-slate-500 mt-3 pt-2 border-t border-slate-100 leading-snug">
                 Karşılaştırmaya en az 10 katılımcısı bulunan
                 sınıflar alınmıştır. Diğer öğrencilerin
                 yanıtları okul geneli hesaplamalara dahildir.
               </p>
            </div>

            {/* KART 2: KARE - MÜDAHALE KAPSAMI (YENİ) */}
            <div className="col-span-1 bg-purple-50 p-5 print:p-5 rounded-2xl border border-purple-100 print:bg-white print:border-slate-300 flex flex-col justify-center shadow-sm print:shadow-none min-h-[140px]">
               <h3 className="font-bold text-purple-900 mb-3 print:mb-2.5 flex items-center text-xs print:text-[11px] uppercase tracking-widest border-b border-purple-200 print:border-slate-200 pb-2">
  <Users className="h-5 w-5 print:h-4 print:w-4 mr-2 text-purple-600"/> Görüşme ve İzlem Grupları
</h3>
               <div className="space-y-2 print:space-y-1.5 mt-1">

  <div className="flex justify-between items-center bg-white print:bg-purple-50/50 p-2 rounded-lg border border-purple-100">
    <span className="text-[10px] print:text-[9px] font-bold text-red-600">
      Güvenlik değerlendirmesi
    </span>
    <span className="text-sm print:text-xs font-black text-red-700">
      {data.redAlarmStudents} öğrenci
    </span>
  </div>

  <div className="flex justify-between items-center bg-white print:bg-purple-50/50 p-2 rounded-lg border border-purple-100">
    <span className="text-[10px] print:text-[9px] font-bold text-amber-700">
      Diğer özel dikkat
    </span>
    <span className="text-sm print:text-xs font-black text-amber-700">
      {Math.max(0, data.attentionStudentCount - data.redAlarmStudents)} öğrenci
    </span>
  </div>

  <div className="flex justify-between items-center bg-white print:bg-purple-50/50 p-2 rounded-lg border border-purple-100">
    <span className="text-[10px] print:text-[9px] font-bold text-emerald-700">
      Diğer katılımcılar
    </span>
    <span className="text-sm print:text-xs font-black text-emerald-700">
      {Math.max(0, data.total - data.attentionStudentCount)} öğrenci
    </span>
  </div>

</div>
            </div>

            {/* KART 3: YATAY - AYLIK AKSİYON PLANI */}
            <div className="col-span-2 bg-amber-50 p-4 print:p-4 rounded-2xl border border-amber-100 print:bg-white print:border-slate-300 shadow-sm print:shadow-none flex items-start gap-4">
               <div className="shrink-0 bg-amber-100 print:bg-slate-100 p-3 print:p-2.5 rounded-xl">
                 <AlertTriangle className="h-6 w-6 print:h-5 print:w-5 text-amber-600"/>
               </div>
               <div className="w-full">
                 <h3 className="font-bold text-amber-900 text-xs print:text-[11px] uppercase tracking-widest mb-2 print:mb-1.5 border-b border-amber-200 print:border-slate-200 pb-1.5">PDR Servisi Aylık Aksiyon Planı</h3>
                <ul className="space-y-1.5 print:space-y-1 text-xs print:text-[10px] text-amber-900 print:text-slate-800 font-medium">

  <li className="flex items-start">
    <CheckCircle className="h-4 w-4 print:h-3 print:w-3 mr-1.5 mt-0.5 text-amber-600 shrink-0"/>
    <span>
      <b>Veli:</b>{" "}
      {data.dominantCategory === "Aile ve Çevre"
        ? "Aile yaşamına ilişkin sık bildirilen konular doğrulanarak uygun veli çalışmaları planlanabilir."
        : "Veli bilgilendirme başlıkları, ihtiyaç dağılımı ve görüşme bulgularına göre belirlenebilir."}
    </span>
  </li>

  <li className="flex items-start">
    <CheckCircle className="h-4 w-4 print:h-3 print:w-3 mr-1.5 mt-0.5 text-amber-600 shrink-0"/>
    <span>
      <b>Öğrenci:</b>{" "}
      {data.dominantCategory === "Akran ve Sosyal Yaşam"
        ? "Akran ilişkileri ve sosyal uyum etkinlikleri değerlendirilebilir."
        : data.dominantCategory === "Okul ve Eğitim"
          ? "Okul deneyimi ve eğitim ortamına ilişkin geri bildirimler üzerinden çalışmalar planlanabilir."
          : data.dominantCategory === "Gelecek ve Planlama"
            ? "Kariyer ve hedef belirleme etkinlikleri planlanabilir."
            : "Grup rehberliği içeriği, en sık işaretlenen somut maddelere göre seçilebilir."}
    </span>
  </li>

  {data.attentionStudentCount > 0 && (
    <li className="flex items-start">
      <CheckCircle className="h-4 w-4 print:h-3 print:w-3 mr-1.5 mt-0.5 text-amber-600 shrink-0"/>
      <span>
        <b>Bireysel:</b>{" "}
        Özel dikkat bildirimi bulunan {data.attentionStudentCount} öğrenci
        için görüşme öncelikleri belirlenebilir.
      </span>
    </li>
  )}

  {data.redAlarmStudents > 0 && (
    <li className="flex items-start">
      <CheckCircle className="h-4 w-4 print:h-3 print:w-3 mr-1.5 mt-0.5 text-red-500 shrink-0"/>
      <span>
        <b>Güvenlik:</b>{" "}
        {data.redAlarmStudents} öğrencinin özel güvenlik bildirimi
        gecikmeden, okul prosedürlerine uygun olarak değerlendirilmelidir.
      </span>
    </li>
  )}

</ul>
               </div>
            </div>

            {/* KART 4: YATAY - GÜÇLÜ YÖNLER */}
            {data.safeZones.length > 0 && (
              <div className="col-span-2 bg-emerald-50 p-4 print:p-4 rounded-2xl border border-emerald-100 print:bg-white print:border-slate-300 shadow-sm print:shadow-none">
                 <div className="flex justify-between items-center border-b border-emerald-200 print:border-slate-200 pb-2 mb-3">
                   <h3 className="font-bold text-emerald-900 flex items-center text-xs print:text-[11px] uppercase tracking-widest">
                     <ShieldCheck className="h-5 w-5 print:h-4 print:w-4 mr-2 text-emerald-600"/> İşaretlenen Olumlu Kaynaklar
                   </h3>
                   <span className="text-[10px] print:text-[9px] text-emerald-700 print:text-slate-500 italic">Öğrencilerin bildirdiği olumlu maddeler</span>
                 </div>
                 <div className="grid grid-cols-1 sm:grid-cols-2 print:grid-cols-2 gap-2.5 print:gap-2.5">
                    {data.safeZones.map((item, idx) => (
                      <div key={idx} className="flex items-center bg-white print:bg-emerald-50/40 p-2.5 print:p-2 rounded-lg border border-emerald-100 print:border-emerald-200">
                         <ShieldCheck className="h-4 w-4 print:h-3.5 print:w-3.5 text-emerald-500 mr-2 shrink-0"/>
                         <div className="min-w-0">

  <p className="text-xs print:text-[10px] font-bold text-emerald-900 print:text-slate-800 leading-tight">
    {item.text}
  </p>

  <p className="text-[10px] print:text-[9px] text-emerald-700">
    {item.count} öğrenci · %{item.percent}
  </p>

</div>
                      </div>
                    ))}
                 </div>
              </div>
            )}
          </div>

          {/* KRİTİK MADDELER */}
          <div className="bg-red-50 p-5 print:p-5 rounded-3xl border border-red-200 print:bg-white print:border-t-2 print:border-x-0 print:border-b-0 print:border-red-600 print:shadow-none shrink-0 avoid-break mt-2">
            <h3 className="font-bold text-red-900 mb-4 print:mb-3 flex items-center text-base print:text-xs border-b border-red-200 print:border-red-200 pb-2.5 uppercase tracking-widest">
              <ShieldAlert className="h-5 w-5 print:h-4 print:w-4 mr-2 text-red-600"/> Özel Dikkat Gerektiren Maddeler ({data.criticalItems.length})
            </h3>
            
            {data.criticalItems.length > 0 ? (
              <div className={`grid grid-cols-1 md:grid-cols-2 ${isHighVolumeCritical ? 'print:grid-cols-3' : 'print:grid-cols-2'} gap-3 print:gap-3`}>
                {data.criticalItems.map((item) => (
                  <div key={item.id} className="flex items-center p-3 print:p-2.5 bg-white print:bg-red-50/30 border border-red-100 print:border-red-200 rounded-xl shadow-sm print:shadow-none avoid-break">
                    <div className="w-8 h-8 print:w-7 print:h-7 rounded-lg flex items-center justify-center bg-red-100 text-red-700 font-black text-sm print:text-[11px] border border-red-200 shrink-0 mr-3 print:mr-2.5">
                      {item.count}
                    </div>
                    <div className="flex-1 min-w-0 pr-2">
                      <p className="text-[10px] print:text-[9px] font-bold text-red-500 uppercase truncate mb-0.5">Özel Dikkat</p>
                      <p className="text-sm print:text-xs font-bold text-red-900 leading-snug line-clamp-2">{item.text}</p>
                    </div>
                    <div className="shrink-0 text-right pl-3 print:pl-2.5 border-l border-red-100 print:border-red-200">
                      <p className="text-base print:text-sm font-black text-red-600">%{item.percent}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
               <p className="text-sm text-slate-500 italic py-6 text-center">Bu grupta listelenen özel dikkat maddelerinden işaretlenen bulunmamaktadır.</p>
            )}
          </div>
        </div>
        {/* ==================== 2. SAYFA SONU ==================== */}

       {aiResult && (
  <div
    className="hidden print:block"
    style={{ pageBreakBefore: 'always' }}
  ></div>
)}

        {/* ========================================================= */}
        {/* SAYFA 3: YAPAY ZEKA RAPORU VE İMZALAR                     */}
        {/* ========================================================= */}
        <div className="flex flex-col gap-5 print:gap-5 mt-6 print:mt-0">
          
          <div className={`bg-purple-50 p-6 print:p-0 rounded-3xl print:rounded-none border border-purple-100 print:border-none shadow-sm print:shadow-none flex-1 flex flex-col pt-2 print:pt-4 ${!aiResult ? 'print:hidden' : ''}`}>
            <div className="flex justify-between items-center mb-4 print:mb-3 border-b border-purple-200 print:border-slate-300 pb-3 shrink-0">
              <h3 className="font-bold text-purple-900 flex items-center text-base print:hidden uppercase tracking-widest">
                <Sparkles className="h-5 w-5 mr-2 text-purple-600"/> Yapay Zeka Kurumsal Strateji Raporu
              </h3>
              <h3 className="hidden print:flex items-center font-bold text-slate-900 text-xs uppercase tracking-widest w-full">
                <Brain className="h-5 w-5 mr-2"/> Okul PDR Servisi Eylem Planı ve Değerlendirme
              </h3>
              <button onClick={() => onGenerateAI(title, aiSummaryItems)} disabled={aiLoading} className="px-5 py-2.5 bg-purple-600 text-white rounded-xl font-bold text-xs hover:bg-purple-700 transition shadow-sm print:hidden flex items-center">
                {aiLoading ? <Loader2 className="animate-spin h-4 w-4 mr-2"/> : <Brain className="h-4 w-4 mr-2"/>}
                {aiLoading ? "Analiz Ediliyor..." : "Raporu Oluştur"}
              </button>
            </div>
            {aiResult && (
            <div className="bg-white p-6 rounded-xl text-slate-800 border border-purple-100 print:border-0 print:p-0 print:text-black mt-4">
                <EditableAIBox text={aiResult} />
            </div>
        )}
          </div>

        <div
  className="hidden print:flex justify-between mt-2 pt-2 text-xs text-black px-12 shrink-0 border-t border-slate-300"
  style={{
    breakInside: 'avoid',
    pageBreakInside: 'avoid'
  }}
>
  <div className="text-center">
    <p className="font-bold">Okul Psikolojik Danışmanı</p>
    <p className="mt-4">İmza</p>
  </div>

  <div className="text-center">
    <p className="font-bold">Okul Müdürü</p>
    <p className="mt-4">İmza</p>
  </div>
</div>
        </div>
        {/* ==================== 3. SAYFA SONU ==================== */}

      </div>
    </>
  );
};